// redux/slices/checkoutSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { clearTableIds } from "./tableIdSlice"; // Import the clearTableIds action from tableIdSlice
import { selectUserRoles } from "./userSlice"
import { mapUserRoles } from '../../functions/useRoleMapping';
import { portalApi } from "../api/portalApi";
import axiosInstance from '../../axios/axiosConfig';
import { fetchCheckedOutJobs, checkoutRecordUrl, isCheckedOut as isTruthyCheckedOut } from '../../services/checkoutService';

const normalizeUserName = (value) => String(value || "").trim().toLowerCase();
const getJobRecordForCheckout = (responseData, jobId) => {
  if (!Array.isArray(responseData)) {
    return responseData || null;
  }

  const matchingJob = responseData.find((job) => {
    const recordId = job?.ID ?? job?.ProjectID ?? job?.id ?? job?.projectID;
    return String(recordId) === String(jobId);
  });

  return matchingJob || null;
};

const checkInOtherJobsForCurrentUser = async (currentUser, targetJobId, checkedOutJobs, endpoint, assertCurrent) => {
  const normalizedCurrentUser = normalizeUserName(currentUser);

  if (!normalizedCurrentUser) {
    throw new Error("Current user is required to manage checkout state");
  }

  assertCurrent();

  const jobsToCheckIn = checkedOutJobs.filter((job) => {
    const isCheckedOut = isTruthyCheckedOut(job?.IsCheckedOut);
    const isOwnedByCurrentUser = normalizeUserName(job?.CheckedOutBy) === normalizedCurrentUser;
    const isDifferentJob = String(job?.ID) !== String(targetJobId);

    return isCheckedOut && isOwnedByCurrentUser && isDifferentJob;
  });

  if (jobsToCheckIn.length === 0) {
    return checkedOutJobs;
  }

  const results = await Promise.allSettled(
    jobsToCheckIn.map((job) =>
      axiosInstance.patch(
        checkoutRecordUrl(endpoint, job.ID),
        {
          ID: job.ID,
          CheckedOutBy: null,
          CheckedOutDate: null,
          IsCheckedOut: false,
        }
      )
    )
  );

  const failedCount = results.filter((result) => result.status === "rejected").length;
  if (failedCount > 0) {
    throw new Error(`Failed to check in ${failedCount} previously checked out job(s)`);
  }

  return checkedOutJobs;
};

// Async Thunk to handle checkout API
export const checkoutJob = createAsyncThunk(
  "checkout/checkoutJob",
  async ({ jobId, currentUser }, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();
      const userRoles = selectUserRoles(state);
      const userEmail = state?.user?.userDetails?.username || state?.user?.userDetails?.email;
      const roles = mapUserRoles(userRoles, userEmail, state);

      // PSN users can edit specific sales fields, so they need to be able to
      // enter edit mode and own checkout state when selecting "Edit".
      if (!roles.Admin && !roles.Apps && !roles.Engineer && !roles.PSN) {
        return rejectWithValue({
          error: "Insufficient permissions to checkout job",
          job: null,
        });
      }

      if (!normalizeUserName(currentUser)) {
        return rejectWithValue({
          error: "Current user is required to checkout a job",
          job: null,
        });
      }

      const endpoint = state.apiUrls?.urls?.generalProject;
      const assertCurrent = () => {
        if (!endpoint || getState().apiUrls?.urls?.generalProject !== endpoint || getState().user?.userDetails !== state.user?.userDetails) {
          throw new Error('Checkout context changed. Reload the job and try again.');
        }
      };
      assertCurrent();
      checkoutRecordUrl(endpoint, jobId);
      const checkedOutJobs = await fetchCheckedOutJobs(endpoint);
      assertCurrent();

      // Step 1: Check the target against the complete checked-out collection.
      const jobData = getJobRecordForCheckout(checkedOutJobs, jobId);
      
      // Step 2: Check if already checked out
      if (jobData && isTruthyCheckedOut(jobData.IsCheckedOut)) {
        if (normalizeUserName(jobData.CheckedOutBy) === normalizeUserName(currentUser)) {
          await checkInOtherJobsForCurrentUser(currentUser, jobId, checkedOutJobs, endpoint, assertCurrent);
          assertCurrent();
          return { jobId, success: true, data: jobData, alreadyCheckedOut: true };
        } else {
          return rejectWithValue({
            error: `Job is already checked out by ${jobData.CheckedOutBy}`,
            job: jobData,
            checkedOutBy: jobData.CheckedOutBy,
          });
        }
      }

      await checkInOtherJobsForCurrentUser(currentUser, jobId, checkedOutJobs, endpoint, assertCurrent);
      assertCurrent();
      dispatch(clearTableIds());
      // Step 3: Job is not checked out, proceed with checkout via RTK Query mutation
      const patchResult = await dispatch(
        portalApi.endpoints.patchGeneralProjectInfo.initiate({
          jobId,
          url: checkoutRecordUrl(endpoint, jobId),
          data: {
            ID: jobId,
            CheckedOutBy: currentUser,
            CheckedOutDate: new Date().toISOString(),
            IsCheckedOut: true,
          },
        })
      );

      if (patchResult.error) {
        throw new Error(patchResult.error.data || "Failed to checkout job");
      }

      assertCurrent();
      return { jobId, success: true, data: patchResult.data };
    } catch (err) {
      return rejectWithValue({
        error: err.message || "Unknown error occurred",
        job: null,
      });
    }
  }
);

// Add Check-In Thunk
export const checkInJob = createAsyncThunk(
  "checkout/checkInJob",
  async ({ jobId, currentUser }, { dispatch, getState, rejectWithValue }) => {
    try {
      // Step 1: Find the job in the complete checked-out collection.
      if (!normalizeUserName(currentUser)) throw new Error('Current user is required to check in a job');
      const state = getState();
      const endpoint = state.apiUrls?.urls?.generalProject;
      checkoutRecordUrl(endpoint, jobId);
      const checkedOutJobs = await fetchCheckedOutJobs(endpoint);
      if (getState().apiUrls?.urls?.generalProject !== endpoint || getState().user?.userDetails !== state.user?.userDetails) {
        throw new Error('Checkout context changed. Reload the job and try again.');
      }
      const jobData = getJobRecordForCheckout(checkedOutJobs, jobId);

      if (!jobData) {
        dispatch(clearJob());
        dispatch(clearTableIds());
        return { jobId, alreadyCheckedIn: true };
      }

      // Step 2: Verify the current user owns the checkout
      if (
        isTruthyCheckedOut(jobData.IsCheckedOut) &&
        normalizeUserName(jobData.CheckedOutBy) !== normalizeUserName(currentUser)
      ) {
        return rejectWithValue(
          `Cannot check in job checked out by ${jobData.CheckedOutBy}`
        );
      }

      // Step 3: If not checked out or owned by current user, proceed with check-in
      const patchResult = await dispatch(
        portalApi.endpoints.patchGeneralProjectInfo.initiate({
          jobId,
          url: checkoutRecordUrl(endpoint, jobId),
          data: {
            ID: jobId,
            CheckedOutBy: null,
            CheckedOutDate: null,
            IsCheckedOut: false,
          },
        })
      );

      if (patchResult.error) {
        throw new Error(patchResult.error.data || "Failed to check in the job");
      }

      if (getState().apiUrls?.urls?.generalProject !== endpoint || getState().user?.userDetails !== state.user?.userDetails) {
        throw new Error('Checkout context changed. Reload the job and try again.');
      }
      // Clear job data and table IDs when job is checked in
      dispatch(clearJob());
      dispatch(clearTableIds());

      return patchResult.data;
    } catch (err) {
      return rejectWithValue(err.message || "Unknown error occurred during check-in");
    }
  }
);

const checkoutSlice = createSlice({
  name: "checkout",
  initialState: {
    job: null,
    jobId: null,
    loading: false,
    error: null,
    checkoutStatus: null,
    isCurrentUserOwner: false, // Track if current user owns the checkout
  },
  reducers: {
    clearJob: (state) => {
      state.job = null;
      state.jobId = null;
      state.checkoutStatus = null;
      state.isCurrentUserOwner = false;
    },
    setCheckoutStateFromJobData: (state, action) => {
      const { jobData, currentUser } = action.payload || {};
      const normalizedCurrentUser = normalizeUserName(currentUser);
      const isCheckedOut = isTruthyCheckedOut(jobData?.IsCheckedOut);
      const checkedOutBy = normalizeUserName(jobData?.CheckedOutBy);
      const isCurrentUserOwner =
        isCheckedOut &&
        normalizedCurrentUser &&
        checkedOutBy === normalizedCurrentUser;

      state.job = jobData || null;
      state.jobId = jobData?.ID || jobData?.ProjectID || null;
      state.isCurrentUserOwner = !!isCurrentUserOwner;

      if (!isCheckedOut) {
        state.checkoutStatus = "not checked out";
      } else if (isCurrentUserOwner) {
        state.checkoutStatus = "already checked out";
      } else {
        state.checkoutStatus = "checked out by another";
      }

      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkoutJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkoutJob.fulfilled, (state, action) => {
        state.loading = false;
        state.job = action.payload.data || action.payload;
        state.jobId = action.payload.jobId;
        state.checkoutStatus = action.payload.alreadyCheckedOut 
          ? "already checked out" 
          : "checked out";
        state.error = null;
        state.isCurrentUserOwner = true; // Current user successfully checked out the job
      })
      .addCase(checkoutJob.rejected, (state, action) => {
        state.loading = false;
        state.job = action.payload?.job || null;
        state.error = action.payload?.error || action.payload;
        state.checkoutStatus = action.payload?.checkedOutBy 
          ? "checked out by another" 
          : "checkout failed";
        state.jobId = action.payload?.job?.ID || null;
        state.isCurrentUserOwner = false; // Current user does NOT own the checkout
      })
      .addCase(checkInJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkInJob.fulfilled, (state) => {
        state.loading = false;
        state.checkoutStatus = "not checked out";
        state.error = null;
        state.job = null;
        state.jobId = null;
        state.isCurrentUserOwner = false;
      })
      .addCase(checkInJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearJob, setCheckoutStateFromJobData } = checkoutSlice.actions;

// Selectors
export const selectIsCurrentUserOwner = (state) => state.checkout.isCurrentUserOwner;
export const selectCheckoutJob = (state) => state.checkout.job;
export const selectCheckoutStatus = (state) => state.checkout.checkoutStatus;

export default checkoutSlice.reducer;
